import React, { useMemo } from "react";
import { FiAlertTriangle, FiChevronRight } from "react-icons/fi";
import { AvatarRing } from "../../../common/comms/AvatarRing";
import { useClientProfile } from "../../../common/comms/ClientProfileProvider";
import { useCommsIdentity } from "../../../hooks/comms/useCommsIdentity";
import { TicketActivity } from "./TicketActivity";
import { useClientTicketHistory } from "./useClientTicketHistory";
import { formatAge } from "./supportUtils";
import { getCsat, getDisplayId, isMerged, isResolved } from "./ticketUtils";

/** Section heading. */
const Label = ({ children }) => (
  <p className="m-0 mb-1.5 text-[9px] font-bold uppercase tracking-[0.1em] text-gray-400">
    {children}
  </p>
);

/** One `key → value` line in the subscription block. */
const Field = ({ label, value, tone = "default" }) => (
  <div className="flex justify-between gap-2 py-0.5 text-[11px]">
    <span className="text-gray-400">{label}</span>
    <span
      className={`truncate text-right ${
        tone === "success" ? "font-bold text-success" : "text-gray-800"
      }`}
    >
      {value ?? "—"}
    </span>
  </div>
);

/**
 * Read the client context off a ticket detail payload. ms-agents nests this
 * differently depending on the route, so every plausible spot is probed —
 * the rail must degrade to dashes, never crash the ticket beside it.
 * @param {Object|null} detail
 */
const readContext = (detail) => {
  const ctx = detail?.client_context || detail?.context || detail || {};
  return {
    subscription: ctx.subscription || detail?.subscription || {},
    account: ctx.account || detail?.account || {},
    coaches: ctx.coaches || detail?.coaches || [],
    email: ctx.email || detail?.email || ctx.account?.email || null,
  };
};

/** Coach entries may be strings or objects. */
const coachName = (coach) =>
  typeof coach === "string" ? coach : coach?.name || coach?.full_name || "Coach";

/**
 * Right rail: who is asking, what they pay for, what they've asked before, and
 * everything that has happened to this ticket.
 *
 * The point of the rail is that an agent should never have to leave the thread
 * to answer "is this person on a plan, have they been here before, and did we
 * already let them down?" — the past-tickets CSAT column in particular is how
 * you spot the second complaint from someone whose first one went badly.
 *
 * @param {Object} props
 * @param {string|null} props.clientId
 * @param {string|null} props.roomId
 * @param {Object|null} props.detail - Ticket detail from `getTicket`.
 * @param {boolean} [props.loading]
 * @param {string|null} [props.error]
 * @param {'pane'|'flow'} [props.layout='pane'] - `flow` drops the internal
 *   scroll container for use inside a `Drawer`.
 */
export function RequesterRail({
  clientId,
  roomId,
  detail,
  loading = false,
  error = null,
  layout = "pane",
}) {
  const { getClientName } = useCommsIdentity();
  const { openClientProfile } = useClientProfile();
  const { subscription, account, coaches, email } = readContext(detail);
  const { tickets: history, loading: historyLoading } = useClientTicketHistory(clientId);
  const isFlow = layout === "flow";

  const name =
    detail?.client_name ||
    detail?.ticket?.client_name ||
    getClientName(clientId) ||
    "Requester";

  // Averaged from the history rather than trusted from a field — there is no
  // lifetime-CSAT endpoint, and a wrong number here misreads the relationship.
  const lifetimeCsat = useMemo(() => {
    const scores = history.map(getCsat).filter((s) => s != null).map(Number);
    if (scores.length === 0) return null;
    return (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);
  }, [history]);

  return (
    <aside
      className={`w-full bg-rail-bg ${isFlow ? "" : "flex h-full min-h-0 flex-col"}`}
      aria-label="Requester context"
    >
      {/* The whole header opens the full profile — the same panel the Users
          tab shows — so "who is this and what are they paying for?" is one
          click from the ticket rather than a trip to another screen. */}
      <button
        type="button"
        disabled={!clientId}
        onClick={() => clientId && openClientProfile(clientId, name)}
        className="flex w-full flex-shrink-0 items-center gap-2.5 border-b border-gray-200 bg-white p-3 text-left transition-colors enabled:hover:bg-gray-50 disabled:cursor-default"
      >
        <AvatarRing name={name} colorIndex={3} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="m-0 truncate text-[13px] font-bold text-gray-900">{name}</p>
          {email && <p className="m-0 truncate text-[10px] text-gray-500">{email}</p>}
        </div>
        {clientId && (
          <FiChevronRight aria-hidden="true" size={14} className="shrink-0 text-gray-400" />
        )}
      </button>

      <div
        className={
          isFlow ? "space-y-4 p-3" : "min-h-0 flex-1 space-y-4 overflow-y-auto p-3"
        }
      >
        {loading && <p className="text-xs text-gray-400">Loading requester context…</p>}
        {error && !loading && <p className="text-xs text-danger-dark">{error}</p>}

        <section>
          <Label>Subscription</Label>
          <Field label="Plan" value={subscription.tier || subscription.plan} />
          <Field
            label="Status"
            value={subscription.status}
            tone={subscription.status === "active" ? "success" : "default"}
          />
          <Field label="Renews" value={subscription.renews_at || subscription.renews} />
          <Field label="Platform" value={account.platform} />
          <Field
            label="Coach"
            value={coaches.length > 0 ? coaches.map(coachName).join(", ") : null}
          />
          <Field
            label="Lifetime CSAT"
            value={lifetimeCsat == null ? null : `${lifetimeCsat} / 5`}
          />
        </section>

        <section>
          <Label>
            Past tickets{history.length > 0 ? ` · ${history.length}` : ""}
          </Label>
          {historyLoading && history.length === 0 ? (
            <p className="m-0 text-[11px] text-gray-400">Loading…</p>
          ) : history.length === 0 ? (
            <p className="m-0 text-[11px] text-gray-400">First time they've written in.</p>
          ) : (
            history.map((t, i) => {
              const csat = getCsat(t);
              return (
                <div
                  key={getDisplayId(t) || i}
                  className="flex gap-1.5 py-0.5 text-[11px]"
                >
                  <span className="font-mono text-gray-400">{getDisplayId(t)}</span>
                  <span className="min-w-0 flex-1 truncate text-gray-700">
                    {t.subject || "(no subject)"}
                  </span>
                  <span
                    className={
                      isMerged(t)
                        ? "text-gray-400"
                        : csat != null
                        ? "text-success"
                        : "text-gray-400"
                    }
                  >
                    {isMerged(t)
                      ? "merged"
                      : csat != null
                      ? `★${csat}`
                      : isResolved(t)
                      ? "closed"
                      : "open"}
                  </span>
                </div>
              );
            })
          )}
        </section>

        <section>
          <Label>Audit trail</Label>
          <TicketActivity roomId={roomId} />
        </section>

        {account.joined || account.created_at ? (
          <p className="m-0 text-[10px] text-gray-400">
            Customer since {formatAge(account.joined || account.created_at)} ago
          </p>
        ) : null}
      </div>

      {/* Coaching-privacy notice — support never sees coaching room content. */}
      <div className={`border-t border-internal/30 bg-internal-bg p-3 ${isFlow ? "" : "flex-shrink-0"}`}>
        <p className="m-0 flex items-start gap-1.5 text-[10px] leading-snug text-internal-text">
          <FiAlertTriangle aria-hidden="true" className="mt-0.5 flex-shrink-0" />
          <span>
            Coaching room content is not visible to support. Coach names shown for
            context only.
          </span>
        </p>
      </div>
    </aside>
  );
}

export default RequesterRail;
